import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { App } from '@/app/App'
import { BootstrapError } from '@/app/BootstrapError'
import { createAppServices } from '@/app/services'
import { EnvironmentValidationError, getEnv } from '@/platform/config/env'
import { getSupabaseClient } from '@/platform/supabase/client'

import './app/index.css'

/**
 * Application bootstrap.
 *
 * Environment validation runs first and fails fast. The failure is caught only
 * so the developer sees the specific variable that is wrong instead of a blank
 * page — the app still refuses to start.
 */
function bootstrap(): React.JSX.Element {
  let env: ReturnType<typeof getEnv>
  try {
    env = getEnv()
  } catch (error) {
    if (error instanceof EnvironmentValidationError) {
      return <BootstrapError title="Configuration error" issues={error.issues} />
    }
    throw error
  }

  let client: ReturnType<typeof getSupabaseClient>
  try {
    client = getSupabaseClient()
  } catch (error) {
    return (
      <BootstrapError
        title="Supabase client error"
        issues={[error instanceof Error ? error.message : 'Unknown error']}
      />
    )
  }

  return <App services={createAppServices(client, env)} />
}

const container = document.getElementById('root')
if (container === null) {
  throw new Error('Root container #root was not found in index.html')
}

createRoot(container).render(<StrictMode>{bootstrap()}</StrictMode>)
